// profitTooltip.js — shared pieces of the per-channel profit hover panel.
// Extracted 2026-10-03 (revamp §5.4): CpApprovalsPage.CpProfitTooltip and
// ProfitMeasurerPage.ProfitTooltipPanel built the identical MRP -> Disc -> SP -> channel
// expense -> Return -> CP -> Profit row list from differently-shaped inputs. The row list,
// header markup, shell style and flip-to-stay-on-screen anchor now live here; each caller
// still maps its own input shape (CP: calcProfit result `p`; PM: Styles-table `row`) onto
// the same arguments, so behaviour of both is unchanged.
import React, { useRef, useEffect } from "react";
import { n0c, n2c } from "./profitFormat";
import { CH_COMPLEX } from "./profitCalcClient";

export const TOOLTIP_SHELL = {
  position: "fixed", zIndex: 9999, pointerEvents: "none",
  background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14,
  padding: "14px 16px", boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
};

export function useTooltipAnchor(x, y, fallbackDims) {
  const ref = useRef();
  const dims = useRef(fallbackDims);
  useEffect(() => { if (ref.current) dims.current = { w: ref.current.offsetWidth, h: ref.current.offsetHeight }; });
  const OFFSET = 18;
  const vw = window.innerWidth, vh = window.innerHeight;
  const { w, h } = dims.current;
  const goRight = x + OFFSET + w <= vw, goDown = y + OFFSET + h <= vh;
  const left = goRight ? x + OFFSET : x - w - OFFSET;
  const top = goDown ? y + OFFSET : y - h - OFFSET;
  return {
    ref,
    safeLeft: Math.max(4, Math.min(left, vw - w - 4)),
    safeTop: Math.max(4, Math.min(top, vh - h - 4)),
  };
}

// cd = channel's calc result (_ch[ch] / channels[ch]). rsp/disc/sp/spNoTax/gst/cp are passed
// in by the caller exactly as it previously read them off its own row shape.
export function buildChannelProfitRows({ cd, ch, rsp, disc, sp, spNoTax, gst, cp }) {
  const isComplex = CH_COMPLEX.includes(ch);
  const isOverride = cd.styleReturnPct > 0 && cd.effReturnPct === cd.styleReturnPct && cd.styleReturnPct > (cd.productReturnPct || 0);
  const hasReturnData = (cd.salesQty || 0) > 0 || (cd.productReturnPct || 0) > 0;
  const qty = cd.salesQty || 0;
  const totalProfit = qty > 0 ? cd.profit * qty : null;
  const rows = [
    { label: "MRP", val: `₹${n0c(rsp)}`, color: "var(--text-muted)" },
    { label: "Disc (Current)", val: `${Number(disc).toFixed(2)}%  −₹${n0c(rsp * (disc / 100))}`, color: "var(--accent)" },
    { label: "SP (Current)", val: `₹${n0c(sp)}`, color: "var(--text)", bold: true },
    { label: `SP ex-Tax (GST ${gst}%)`, val: `₹${n2c(spNoTax)}`, color: "var(--text)", sep: true },
    ...(isComplex ? [
      { label: `GT (Lvl ${cd.level})`, val: `−₹${n2c(cd.gt)}`, color: "var(--danger)" },
      { label: "Commission", val: `−₹${n2c(cd.comm)}`, color: "var(--danger)" },
      { label: "Fixed Fee", val: `−₹${n2c(cd.ff)}`, color: "var(--danger)" },
      { label: "Wtd RL Charge", val: `₹${n2c(cd.weightedCharge)}`, color: "var(--text-muted)" },
    ] : [
      { label: `Commission (${cd.basis || "GOT"})`, val: `−₹${n2c(cd.commAmt)}`, color: "var(--danger)" },
      { label: "Freight", val: `−₹${n2c(cd.freightAmt)}`, color: "var(--danger)" },
    ]),
    {
      label: `Return (${cd.returnQty ?? "?"}pcs × ₹${isComplex ? n2c(cd.weightedCharge) : n2c(cd.freightAmt)} · ${hasReturnData ? `${Number(cd.effReturnPct || 0).toFixed(2)}% ${isOverride ? "★Style" : "Prod"}` : "no data"})`,
      val: `−₹${isComplex ? n2c(cd.rlCostPerUnit) : n2c(cd.returnCost)}`, color: "#e11d48",
    },
    { label: "CP", val: `−₹${n2c(cp)}`, color: "var(--danger)", sep: true },
    { label: "Profit/Unit (Current)", val: `₹${n2c(cd.profit)}`, color: cd.profit >= 0 ? "var(--success)" : "var(--danger)", bold: true },
  ];
  if (qty > 0) {
    rows.push({ label: "Sales Qty", val: `× ${qty}`, color: "var(--text-muted)", sep: true });
    rows.push({ label: "Total Profit (Current)", val: `₹${n0c(totalProfit)}`, color: totalProfit >= 0 ? "var(--success)" : "var(--danger)", bold: true });
  }
  return rows;
}

export function ProfitTooltipBody({ styleNo, chLabel, profit, rows }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, paddingBottom: 8, borderBottom: "1px solid var(--border)" }}>
        <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, color: "var(--text)" }}>{styleNo}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 5, background: profit >= 0 ? "var(--success-bg)" : "var(--danger-bg)", color: profit >= 0 ? "var(--success)" : "var(--danger)" }}>
          {chLabel}
        </span>
      </div>
      {rows.map((r, i) => (
        <div key={i}>
          {r.sep && <div style={{ height: 1, background: "var(--border)", margin: "6px 0" }} />}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0", gap: 10 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{r.label}</span>
            <span style={{ fontSize: 12, fontFamily: "monospace", fontWeight: r.bold ? 700 : 500, color: r.color, whiteSpace: "nowrap" }}>{r.val}</span>
          </div>
        </div>
      ))}
    </>
  );
}
